import {
  Badge,
  Col,
  Button,
  Icon,
  IconNames,
  Row,
  Typography,
} from '@repo/ui/components'
import { PaymentType, ToDateTime } from '@repo/shared/components'
import { type MessagesKeys, useTranslate } from '@repo/shared/hooks'
import { CSSVariableColor } from '@repo/ui/types/colors'
import { UserFiatTransaction } from '@repo/services'
import { BalanceDetailsDialog } from './balance-details.dialog'
import { useIsDesktop } from '@repo/ui/hooks'
import { cn } from '@repo/ui/lib'

const iconMapper: Record<'deposit' | 'withdraw', IconNames> = {
  deposit: 'PlusLine',
  withdraw: 'TriangleArrowUpLine',
}

type TColors =
  | 'neutral'
  | 'base'
  | 'positive'
  | 'negative'
  | 'alert'
  | 'disabled'
  | null
  | undefined

export type TStatus =
  | Exclude<UserFiatTransaction['status'], undefined>
  | 'rejected'

const iconColorMapper: Record<'deposit' | 'withdraw', CSSVariableColor> = {
  deposit: 'fill-positive-surface-100',
  withdraw: 'fill-negative-surface-100',
}

// TODO: remove cancelled from here
export const statusColorMapper: Record<TStatus | 'cancelled', TColors> = {
  completed: 'positive',
  failed: 'negative',
  rejected: 'negative',
  pending: 'alert',
  canceled: 'neutral',
  cancelled: 'neutral',
}

export const BalanceItem = (props: UserFiatTransaction) => {
  const { amount, status, createdAt, deposit, withdrawal } = props

  const { t } = useTranslate()
  const isDesktop = useIsDesktop()
  const type = deposit ? 'deposit' : 'withdraw'

  const title = deposit ? t('walletPage_deposit') : t('walletPage_withdraw')

  return (
    <BalanceDetailsDialog {...props}>
      <Col>
        <Row
          className={cn('justify-between gap-md pt-xl cursor-pointer pb-xl')}
        >
          <Icon
            name={iconMapper[type]}
            color={iconColorMapper[type]}
            size='sm'
          />
          <Col className='gap-xs item-start text-start cursor-pointer'>
            <Row className='gap-xs flex-wrap max-w-[150px] md:max-w-[unset]'>
              <Typography weight='bold' size='sm'>
                {title}
              </Typography>
              {status !== 'completed' && (
                <Badge
                  variant={status === 'pending' ? 'solid' : 'liquid'}
                  color={statusColorMapper[status as TStatus]}
                >
                  {t(`walletPage_${status}` as MessagesKeys)}
                </Badge>
              )}
            </Row>
            {deposit?.type && (
              <Typography size='xs' color='text-neutral-surface-100'>
                {t(`depositType_${deposit.type}` as MessagesKeys)}
              </Typography>
            )}
            {withdrawal?.iban && (
              <Typography
                variant='bdi'
                size='xs'
                color='text-neutral-surface-100'
                className='max-w-[160px] md:max-w-[unset] text-ellipsis overflow-hidden whitespace-nowrap rtl:text-right'
              >
                {withdrawal?.iban}
              </Typography>
            )}
          </Col>
          <Col className='items-end gap-xs'>
            <Row className='gap-xs w-fit '>
              {status === 'pending' && isDesktop && (
                <Button
                  color='negative'
                  variant='ghost'
                  size='xs'
                  className='px-xs'
                >
                  {t(`walletPage_cancel`)}
                </Button>
              )}
              <PaymentType size='xs' numberSize='sm' value={amount} />
            </Row>

            <Typography size='xs' color='text-neutral-surface-80' variant='bdi'>
              <ToDateTime date={new Date(createdAt ?? '')} />
            </Typography>
          </Col>
        </Row>
        {status === 'pending' && !isDesktop && (
          <Row className='w-full'>
            <Button
              color='negative'
              variant='ghost'
              size='xs'
              className='px-xs my-xs '
              width='full'
            >
              {t(`walletPage_cancel`)}
            </Button>
          </Row>
        )}
      </Col>
    </BalanceDetailsDialog>
  )
}
